import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link } from '#router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePageTitle } from '@/hooks/use-document-title'
import { SPRINT_NAME_MAX_LENGTH } from '@/lib/sprint-name/validate'
import { type RouterOutputs, trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { Tabs } from '../coaching/tabs'
import {
  type Consequence,
  describeAdd,
  describeGrant,
  describeReduce,
} from './sprint-consequences'
import {
  formatDay,
  formatLockLineFor,
  formatWeek,
  PHASE_LABEL,
  type SprintUiPhase,
  uiPhaseOf,
} from './sprint-format'
import { SprintBadges } from './sprints-list'

type SprintDetail = RouterOutputs['admin']['sprints']['get']
type PersonRow = SprintDetail['roster'][number]

type Pool = 'in' | 'out'
/**
 * What ticking rows will do. In the Sprint, an admin grants a credit or, once
 * the week is running, flags reduced requirements: removal closed with
 * LG-1073 (the week is released and pinned from `startsAt`). Out of it, an
 * admin adds, or grants to a client the projection says will be unfunded.
 */
type Action = 'add' | 'grant' | 'reduce'

/** `Tabs` hands back a string; only the two ids above may reach `setPool`. */
function isPool(id: string): id is Pool {
  return id === 'in' || id === 'out'
}
type SortKey = 'health' | 'client' | 'credits'
type SortDir = 'asc' | 'desc'

const WEEK_LABEL: Record<PersonRow['week'], string> = {
  planned: 'Week planned',
  empty: 'Week is empty',
  missing: 'No week at all',
}

const STATUS_LABEL: Record<PersonRow['status'], string> = {
  upcoming: 'In, not yet locked',
  locked: 'Locked in',
  skipped: 'Skipped',
  unfunded: 'Unfunded at lock-in',
  absent: 'Never in this Sprint',
}

const WEEK_RANK: Record<PersonRow['week'], number> = {
  planned: 0,
  empty: 1,
  missing: 2,
}

/**
 * Worse first: no week, then an empty one; a zero balance adds on top. A
 * week an admin has reduced is not asking for attention on its emptiness.
 */
function healthRank(row: PersonRow): number {
  const week = row.reducedRequirements ? 0 : WEEK_RANK[row.week]
  return week + (row.balance === 0 ? 2 : 0)
}

const COMPARE: Record<SortKey, (a: PersonRow, b: PersonRow) => number> = {
  client: (a, b) => a.clientName.localeCompare(b.clientName),
  credits: (a, b) => a.balance - b.balance,
  health: (a, b) => healthRank(a) - healthRank(b),
}

/** Click once for ascending, again for descending, a third time to reset. */
function nextSort(
  current: { key: SortKey; dir: SortDir },
  key: SortKey,
): { key: SortKey; dir: SortDir } {
  if (current.key !== key) return { key, dir: 'asc' }
  if (current.dir === 'asc') return { key, dir: 'desc' }
  return { key: 'health', dir: 'desc' }
}

export function SprintPage({ sprintId }: { sprintId: string }) {
  const detail = trpc.admin.sprints.get.useQuery({ sprintId })
  usePageTitle(detail.data ? detail.data.sprint.name : 'Sprint')

  if (detail.isLoading) {
    return (
      <div className="text-muted-foreground mx-auto flex max-w-6xl items-center gap-2 px-6 py-10 text-sm">
        <RefreshCw size={16} className="animate-spin" /> Loading Sprint…
      </div>
    )
  }
  if (detail.error || !detail.data) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-10 text-sm">
        <p className="text-destructive">
          {detail.error?.message ?? 'Sprint not found.'}
        </p>
        <Link
          href="/admin/sprints"
          className="mt-2 inline-block hover:underline"
        >
          All Sprints
        </Link>
      </div>
    )
  }
  return <SprintDetailView key={sprintId} detail={detail.data} />
}

function SprintDetailView({ detail }: { detail: SprintDetail }) {
  const { sprint, phase, roster, candidates, neighbours, running } = detail
  const utils = trpc.useUtils()

  const [pool, setPool] = useState<Pool>('in')
  const [action, setAction] = useState<Action>('grant')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'health',
    dir: 'desc',
  })
  const [reason, setReason] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [busy, setBusy] = useState(false)

  const add = trpc.admin.sprints.add.useMutation()
  const grant = trpc.admin.sprints.grantCredit.useMutation()
  const reduce = trpc.admin.sprints.setReducedRequirements.useMutation()

  const people = pool === 'in' ? roster : candidates
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const rows = needle
      ? people.filter((row) => row.clientName.toLowerCase().includes(needle))
      : [...people]
    const direction = sort.dir === 'asc' ? 1 : -1
    const compare = COMPARE[sort.key]
    rows.sort((a, b) => {
      const byKey = compare(a, b)
      return byKey !== 0 ? byKey * direction : COMPARE.client(a, b)
    })
    return rows
  }, [people, search, sort])

  const chosen = people.filter((row) => selected.has(row.engagementId))
  const describe = (row: PersonRow): Consequence =>
    action === 'grant'
      ? describeGrant(row, phase)
      : action === 'reduce'
        ? describeReduce(row, phase)
        : describeAdd(row, sprint, phase)
  const previews = chosen.map((row) => ({ row, consequence: describe(row) }))
  const doable = previews.filter((preview) => preview.consequence.doable)
  const ended = phase === 'after_end'
  const started = phase === 'after_start'

  async function refresh() {
    await Promise.all([
      utils.admin.sprints.get.invalidate({ sprintId: sprint.id }),
      utils.admin.sprints.list.invalidate(),
    ])
  }

  async function clearReduced(row: PersonRow) {
    if (!row.participationId) return
    setBusy(true)
    try {
      await reduce.mutateAsync({
        participationId: row.participationId,
        reduced: false,
      })
      toast.success(`Reduced requirements cleared for ${row.clientName}`)
      await refresh()
    } catch (error) {
      toast.error(
        `${row.clientName}: ${error instanceof Error ? error.message : 'failed'}`,
      )
    } finally {
      setBusy(false)
    }
  }

  function switchPool(next: Pool) {
    setPool(next)
    setAction(next === 'in' ? 'grant' : 'add')
    setSelected(new Set())
  }

  function toggle(engagementId: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(engagementId)) next.delete(engagementId)
      else next.add(engagementId)
      return next
    })
  }

  function toggleAll() {
    setSelected((current) =>
      visible.every((row) => current.has(row.engagementId))
        ? new Set()
        : new Set(visible.map((row) => row.engagementId)),
    )
  }

  function cycleSort(key: SortKey) {
    setSort((current) => nextSort(current, key))
  }

  async function commit() {
    const trimmed = reason.trim()
    if (!trimmed || doable.length === 0) return
    setBusy(true)
    const failures: string[] = []
    for (const { row } of doable) {
      try {
        if (action === 'grant') {
          await grant.mutateAsync({
            engagementId: row.engagementId,
            reason: trimmed,
          })
        } else if (action === 'reduce') {
          // `describeReduce` only marks a row with a participation doable.
          if (!row.participationId) throw new Error('Not in this Sprint')
          await reduce.mutateAsync({
            participationId: row.participationId,
            reduced: true,
            reason: trimmed,
          })
        } else {
          await add.mutateAsync({
            sprintId: sprint.id,
            engagementId: row.engagementId,
            reason: trimmed,
          })
        }
      } catch (error) {
        failures.push(
          `${row.clientName}: ${error instanceof Error ? error.message : 'failed'}`,
        )
      }
    }
    setBusy(false)
    const done = doable.length - failures.length
    if (done > 0) {
      toast.success(
        action === 'grant'
          ? `${done} ${done === 1 ? 'credit' : 'credits'} granted`
          : action === 'reduce'
            ? `${done} marked with reduced requirements`
            : `${done} added to ${sprint.name}`,
      )
    }
    for (const failure of failures) toast.error(failure)
    setSelected(new Set())
    if (done > 0) setReason('')
    await refresh()
  }

  const allVisibleSelected =
    visible.length > 0 && visible.every((row) => selected.has(row.engagementId))

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm">
        <nav className="text-muted-foreground flex items-center gap-1">
          <Link
            href="/admin/sprints"
            className="hover:text-foreground hover:underline"
          >
            All Sprints
          </Link>
          <span>/</span>
          <span className="text-foreground">{sprint.name}</span>
        </nav>
        <div className="flex items-center gap-1">
          <NeighbourButton neighbour={neighbours.previous} side="previous" />
          <CurrentSprintButton running={running} sprintId={sprint.id} />
          <NeighbourButton neighbour={neighbours.next} side="next" />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          <div className="border-border bg-card mb-6 rounded-lg border p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-semibold">{sprint.name}</h1>
                  <PhaseBadge
                    phase={uiPhaseOf(phase, running?.id === sprint.id)}
                  />
                  <SprintBadges sprint={sprint} />
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                  {formatWeek(sprint.startsAt, sprint.endsAt)} ·{' '}
                  {formatLockLineFor(phase, sprint)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={renaming ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRenaming((current) => !current)}
                  disabled={ended}
                >
                  Rename
                </Button>
                <DeferredButton
                  label={sprint.isFree ? 'Charge again' : 'Mark free'}
                />
                <DeferredButton
                  label={
                    sprint.status === 'suspended' ? 'Resume Sprint' : 'Suspend'
                  }
                />
              </div>
            </div>
          </div>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <Tabs
              tabs={[
                { id: 'in', label: 'In Sprint', count: roster.length },
                { id: 'out', label: 'Out of Sprint', count: candidates.length },
              ]}
              activeTab={pool}
              onTabChange={(id) => {
                if (isPool(id)) switchPool(id)
              }}
            />
            <div className="relative">
              <Search
                size={14}
                className="text-muted-foreground absolute top-1/2 left-2.5 -translate-y-1/2"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search clients"
                className="h-8 w-56 pl-8"
                aria-label="Search clients"
              />
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-left text-xs uppercase">
              <tr className="border-border border-b">
                <th className="w-8 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all shown"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    disabled={ended || visible.length === 0}
                  />
                </th>
                <SortHeader
                  label="Client"
                  sortKey="client"
                  sort={sort}
                  onSort={cycleSort}
                />
                <th className="py-2 pr-3 font-medium">Engagement began</th>
                {pool === 'out' && (
                  <th className="py-2 pr-3 font-medium">Why out</th>
                )}
                <SortHeader
                  label="Credits"
                  sortKey="credits"
                  sort={sort}
                  onSort={cycleSort}
                  align="right"
                />
                <th className="py-2 font-medium">Week</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-muted-foreground py-8 text-center"
                  >
                    {people.length === 0
                      ? pool === 'in'
                        ? 'Nobody is in this Sprint.'
                        : 'Everyone with an active engagement is already in.'
                      : 'No client matches that search.'}
                  </td>
                </tr>
              ) : (
                visible.map((row) => {
                  const isSelected = selected.has(row.engagementId)
                  // Red means something to do. Out of the Sprint, there is
                  // nothing to do for them here, so those rows stay neutral.
                  const unhealthy = pool === 'in' && healthRank(row) > 0
                  return (
                    <tr
                      key={row.engagementId}
                      className={cn(
                        'border-border border-b',
                        unhealthy && 'bg-destructive/5',
                        isSelected && 'bg-primary/5',
                      )}
                    >
                      <td className="py-2">
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.clientName}`}
                          checked={isSelected}
                          onChange={() => toggle(row.engagementId)}
                          disabled={ended}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <Link
                          href={`/admin/coaching/client/${row.clientId}`}
                          className="font-medium hover:underline"
                        >
                          {row.clientName}
                        </Link>
                      </td>
                      <td className="text-muted-foreground py-2 pr-3">
                        {formatDay(row.engagementStartsOn)}
                      </td>
                      {pool === 'out' && (
                        <td className="text-muted-foreground py-2 pr-3">
                          {row.willBeUnfunded
                            ? 'Out of credits'
                            : STATUS_LABEL[row.status]}
                        </td>
                      )}
                      <td
                        className={cn(
                          'py-2 pr-3 text-right tabular-nums',
                          pool === 'in' &&
                            row.balance === 0 &&
                            'text-destructive font-medium',
                        )}
                      >
                        {row.balance}
                      </td>
                      <td
                        className={cn(
                          'py-2',
                          pool === 'in' &&
                            row.week !== 'planned' &&
                            !row.reducedRequirements &&
                            'text-destructive',
                        )}
                      >
                        {WEEK_LABEL[row.week]}
                        {row.reducedRequirements && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            {/* The reason is the whole point of the badge, so
                                it hangs off a real button rather than a
                                `title`: hover-only would hide it from
                                keyboard and screen readers, and truncate it. */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge asChild variant="outline">
                                  <button type="button">Reduced</button>
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent variant="panel">
                                {row.reducedRequirements.reason}
                              </TooltipContent>
                            </Tooltip>
                            {!ended && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-6 px-1 text-xs"
                                disabled={busy}
                                aria-label={`Clear reduced requirements for ${row.clientName}`}
                                onClick={() => clearReduced(row)}
                              >
                                Clear
                              </Button>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
          <p className="text-muted-foreground mt-2 text-xs">
            {sort.key === 'health'
              ? 'Sorted with the rows needing attention first.'
              : `Sorted by ${sort.key}, ${sort.dir === 'asc' ? 'ascending' : 'descending'}. Click again to reset.`}
          </p>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          {renaming ? (
            <RenameForm sprint={sprint} onDone={() => setRenaming(false)} />
          ) : (
            <ActionPanel
              pool={pool}
              started={started}
              action={action}
              onAction={(next) => {
                setAction(next)
                setSelected(new Set())
              }}
              ended={ended}
              previews={previews}
              doableCount={doable.length}
              reason={reason}
              onReason={setReason}
              busy={busy}
              onCommit={commit}
            />
          )}
        </aside>
      </div>
    </div>
  )
}

function PhaseBadge({ phase }: { phase: SprintUiPhase }) {
  return (
    <Badge
      variant={phase === 'running' ? 'default' : 'outline'}
      className={cn(phase === 'running' && 'bg-[#51B0BC] text-white')}
    >
      {PHASE_LABEL[phase]}
    </Badge>
  )
}

/** The pager names its destinations: "‹ Sprint 12" beats "‹ Previous". */
function NeighbourButton({
  neighbour,
  side,
}: {
  neighbour: { id: string; name: string } | null
  side: 'previous' | 'next'
}) {
  const Icon = side === 'previous' ? ChevronLeft : ChevronRight
  if (!neighbour) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        aria-label={`No ${side} Sprint`}
      >
        <Icon size={16} />
      </Button>
    )
  }
  return (
    <Button asChild variant="outline" size="sm">
      <Link
        href={`/admin/sprints/${neighbour.id}`}
        aria-label={`${side} Sprint: ${neighbour.name}`}
      >
        {side === 'previous' && <Icon size={16} />}
        {neighbour.name}
        {side === 'next' && <Icon size={16} />}
      </Link>
    </Button>
  )
}

/** Always between the pager's arrows; disabled once you are on the running week. */
function CurrentSprintButton({
  running,
  sprintId,
}: {
  running: { id: string; name: string } | null
  sprintId: string
}) {
  if (running && running.id !== sprintId) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button asChild variant="outline" size="sm">
            <Link href={`/admin/sprints/${running.id}`}>Current Sprint</Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent variant="panel">{running.name}</TooltipContent>
      </Tooltip>
    )
  }
  // A disabled button gets no hover, so the reason rides on the title.
  return (
    <Button
      variant="outline"
      size="sm"
      disabled
      title={running ? 'You are on the current Sprint' : 'No Sprint is running'}
    >
      Current Sprint
    </Button>
  )
}

/**
 * Suspend and Mark free are LG-1078's; the button is here so the gap is
 * visible. A disabled button swallows pointer events, so the tooltip hangs
 * off a wrapping span.
 */
function DeferredButton({ label }: { label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button variant="outline" size="sm" disabled>
            {label}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent variant="panel">
        Not built yet: see LG-1078
      </TooltipContent>
    </Tooltip>
  )
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string
  sortKey: SortKey
  sort: { key: SortKey; dir: SortDir }
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
}) {
  const active = sort.key === sortKey
  return (
    <th
      aria-sort={
        active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      className={cn('py-2 pr-3 font-medium', align === 'right' && 'text-right')}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="hover:text-foreground inline-flex items-center gap-1 uppercase"
      >
        {label}
        {active &&
          (sort.dir === 'asc' ? (
            <ArrowUp size={12} />
          ) : (
            <ArrowDown size={12} />
          ))}
      </button>
    </th>
  )
}

const ACTION_TITLE: Record<Action, string> = {
  add: 'Add to this Sprint',
  grant: 'Grant a credit',
  reduce: 'Reduced requirements',
}

const ACTION_VERB: Record<Action, string> = {
  add: 'Add',
  grant: 'Grant',
  reduce: 'Reduce',
}

function ActionPanel({
  pool,
  started,
  action,
  onAction,
  ended,
  previews,
  doableCount,
  reason,
  onReason,
  busy,
  onCommit,
}: {
  pool: Pool
  /** After `startsAt`: the one phase reduced requirements can be set in. */
  started: boolean
  action: Action
  onAction: (next: Action) => void
  ended: boolean
  previews: { row: PersonRow; consequence: Consequence }[]
  doableCount: number
  reason: string
  onReason: (value: string) => void
  busy: boolean
  onCommit: () => void
}) {
  const verb = ACTION_VERB[action]
  const skipped = previews.length - doableCount
  // Out of the Sprint there are two things to do for a client: put them in,
  // or fund the week the lock sweep would leave them out of. In a running
  // Sprint there are two as well: a credit, or reduced requirements.
  const options: readonly Action[] | null = ended
    ? null
    : pool === 'out'
      ? ['add', 'grant']
      : started
        ? ['grant', 'reduce']
        : null
  return (
    <div className="border-border bg-card rounded-lg border p-4 text-sm">
      <h2 className="font-semibold">{ACTION_TITLE[action]}</h2>
      {options && (
        <div className="mt-2 flex gap-1" role="group" aria-label="Action">
          {options.map((option) => (
            <Button
              key={option}
              type="button"
              size="sm"
              variant={action === option ? 'default' : 'outline'}
              aria-pressed={action === option}
              onClick={() => onAction(option)}
            >
              {ACTION_TITLE[option]}
            </Button>
          ))}
        </div>
      )}
      {ended ? (
        <p className="text-muted-foreground mt-2">
          This Sprint has ended. Its roster is history now.
        </p>
      ) : previews.length === 0 ? (
        <p className="text-muted-foreground mt-2">
          Tick clients in the table. What each move does is spelled out here
          before you commit it.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {previews.map(({ row, consequence }) => (
            <li key={row.engagementId} className="flex gap-2">
              {consequence.doable ? (
                <Check size={16} className="mt-0.5 shrink-0 text-[#51B0BC]" />
              ) : (
                <AlertTriangle
                  size={16}
                  className="text-destructive mt-0.5 shrink-0"
                />
              )}
              <div>
                <p>{consequence.text}</p>
                {consequence.ledger && (
                  <p className="text-muted-foreground mt-0.5 font-mono text-xs">
                    {consequence.ledger}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!ended && (
        <form
          className="mt-4 space-y-2"
          onSubmit={(event) => {
            event.preventDefault()
            onCommit()
          }}
        >
          <Label htmlFor="sprint-action-reason">Reason</Label>
          <Textarea
            id="sprint-action-reason"
            value={reason}
            onChange={(event) => onReason(event.target.value)}
            placeholder="Kept with the participation event for whoever reads it next."
            rows={3}
            maxLength={500}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={busy || doableCount === 0 || reason.trim().length === 0}
          >
            {busy ? 'Working…' : `${verb} · ${doableCount}`}
          </Button>
          {skipped > 0 && (
            <p className="text-muted-foreground text-xs">
              {skipped} of {previews.length} selected will be skipped.
            </p>
          )}
        </form>
      )}
    </div>
  )
}

function RenameForm({
  sprint,
  onDone,
}: {
  sprint: SprintDetail['sprint']
  onDone: () => void
}) {
  const utils = trpc.useUtils()
  const rename = trpc.admin.sprints.rename.useMutation()
  const [name, setName] = useState(sprint.name)
  const [reason, setReason] = useState('')
  const trimmed = name.trim()
  const changed = trimmed.length > 0 && trimmed !== sprint.name

  const canSubmit = !rename.isPending && changed && reason.trim().length > 0

  async function submit() {
    if (!canSubmit) return
    try {
      await rename.mutateAsync({
        sprintId: sprint.id,
        name: trimmed,
        reason: reason.trim(),
      })
      toast.success(`Renamed to ${trimmed}`)
      await Promise.all([
        utils.admin.sprints.get.invalidate({ sprintId: sprint.id }),
        utils.admin.sprints.list.invalidate(),
      ])
      onDone()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Rename failed')
    }
  }

  return (
    <div className="border-border bg-card rounded-lg border p-4 text-sm">
      <h2 className="font-semibold">Rename this Sprint</h2>
      <p className="text-muted-foreground mt-1">
        Clients see the new name everywhere, including in mail already queued.
      </p>
      <form
        className="mt-4 space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <Label htmlFor="sprint-rename-name">Name</Label>
        <Input
          id="sprint-rename-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={SPRINT_NAME_MAX_LENGTH}
        />
        <Label htmlFor="sprint-rename-reason">Reason</Label>
        <Textarea
          id="sprint-rename-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          maxLength={500}
        />
        <div className="flex gap-2">
          <Button type="submit" className="flex-1" disabled={!canSubmit}>
            {rename.isPending ? 'Renaming…' : 'Rename'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
